// components/AnimatedTimeline.js
import React, { useEffect, useRef, useState } from 'react';

const TimelineItem = ({ date, title, description, isEven, index, updateVisibility }) => {
  const itemRef = useRef(null);

  useEffect(() => {
    const currentItemRef = itemRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('timeline-item-visible');
            updateVisibility(index, true);
          } else {
            entry.target.classList.remove('timeline-item-visible');
            updateVisibility(index, false);
          }
        });
      },
      { 
        threshold: 0.2,
        rootMargin: '0px 0px -100px 0px' // Adjust this value to control when the animation triggers
      }
    );

    if (currentItemRef) {
      observer.observe(currentItemRef);
    }

    return () => {
      if (currentItemRef) {
        observer.unobserve(currentItemRef);
      }
    };
  }, [index, updateVisibility]);

  return (
    <div 
      ref={itemRef}
      className={`timeline-item ${isEven ? 'timeline-item-even' : 'timeline-item-odd'}`}
    >
      <div className="timeline-icon">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
        </svg>
      </div>
      <div className="timeline-content">
        <div className="timeline-date">{date}</div>
        <h3 className="text-xl font-bold text-gray-900 mb-3">{title}</h3>
        <p className="text-gray-700">{description}</p>
      </div>
    </div>
  );
};

const AnimatedTimeline = () => {
  const timelineRef = useRef(null);
  const lineRef = useRef(null);
  const [visibleItems, setVisibleItems] = useState([]);

  const updateVisibility = (index, isVisible) => {
    setVisibleItems(prev => {
      const newState = [...prev];
      newState[index] = isVisible;
      return newState;
    });
  };

  useEffect(() => {
    const timelineData = [
      {
        date: "Q1 2020",
        title: "Our Beginning",
        description: "Founded in 2020, 3D PrintTech began with a simple yet powerful vision: to provide exceptional 3D printing services that bring ideas to life and help businesses and individuals create objects that matter."
      },
      {
        date: "Q2 2020",
        title: "First Generation Products",
        description: "We delivered the first generation of our products, which came with control and slicing. We applied for a US patent and gained interest from multiple 3D printer manufacturers."
      },
      {
        date: "Q3 2020",
        title: "Expansion Phase",
        description: "Our workshop expanded from a single printer to a comprehensive 3D printing service with state-of-the-art equipment and a talented team dedicated to bringing your ideas to reality."
      },
      {
        date: "Q1 2021",
        title: "Research & Development",
        description: "Secured $250,000 funding from government grants to support our R&D research. Started developing proprietary technologies to improve print quality and efficiency."
      },
      {
        date: "Today",
        title: "Industry Leadership",
        description: "Today, we continue to push the boundaries of what's possible with 3D printing technology, helping our clients create everything from functional prototypes to custom design pieces."
      }
    ];

    // Initialize visible items array
    setVisibleItems(Array(timelineData.length).fill(false));
  }, []);

  useEffect(() => {
    const updateLineHeight = () => {
      if (!lineRef.current || !timelineRef.current) return;
      
      // Find the first and last visible items
      const firstVisibleIndex = visibleItems.findIndex(item => item === true);
      const lastVisibleIndex = visibleItems.lastIndexOf(true);
      
      if (firstVisibleIndex === -1 || lastVisibleIndex === -1) {
        // No items visible, hide line
        lineRef.current.style.transform = 'scaleY(0)';
        return;
      }
      
      const timelineRect = timelineRef.current.getBoundingClientRect();
      const allItems = timelineRef.current.querySelectorAll('.timeline-item');
      
      if (allItems.length === 0) return;
      
      // Get the positions of the first and last visible items
      const firstItemRect = allItems[firstVisibleIndex].getBoundingClientRect();
      const lastItemRect = allItems[lastVisibleIndex].getBoundingClientRect();
      
      // Calculate the visible portion of the line
      const lineTop = (firstItemRect.top + firstItemRect.height / 2 - timelineRect.top) / timelineRect.height;
      const lineBottom = (lastItemRect.top + lastItemRect.height / 2 - timelineRect.top) / timelineRect.height;
      
      // Calculate the scale and translation
      const scaleY = lineBottom - lineTop;
      const translateY = lineTop * 100;
      
      // Apply the transformation
      lineRef.current.style.transform = `translateY(${translateY}%) scaleY(${scaleY})`;
      lineRef.current.style.transformOrigin = 'top';
    };

    // Update line height whenever visible items change
    updateLineHeight();
    
    // Also update on scroll for smoother animation
    const handleScroll = () => {
      requestAnimationFrame(updateLineHeight);
    };
    
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', updateLineHeight);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', updateLineHeight);
    };
  }, [visibleItems]);

  const timelineData = [
    {
      date: "Q1 2020",
      title: "Our Beginning",
      description: "Founded in 2020, 3D PrintTech began with a simple yet powerful vision: to provide exceptional 3D printing services that bring ideas to life and help businesses and individuals create objects that matter."
    },
    {
      date: "Q2 2020",
      title: "First Generation Products",
      description: "We delivered the first generation of our products, which came with control and slicing. We applied for a US patent and gained interest from multiple 3D printer manufacturers."
    },
    {
      date: "Q3 2020",
      title: "Expansion Phase",
      description: "Our workshop expanded from a single printer to a comprehensive 3D printing service with state-of-the-art equipment and a talented team dedicated to bringing your ideas to reality."
    },
    {
      date: "Q1 2021",
      title: "Research & Development",
      description: "Secured $250,000 funding from government grants to support our R&D research. Started developing proprietary technologies to improve print quality and efficiency."
    },
    {
      date: "Today",
      title: "Industry Leadership",
      description: "Today, we continue to push the boundaries of what's possible with 3D printing technology, helping our clients create everything from functional prototypes to custom design pieces."
    }
  ];

  return (
    <div className="timeline-container" ref={timelineRef}>
      <div className="timeline-line" ref={lineRef}></div>
      
      {timelineData.map((item, index) => (
        <TimelineItem
          key={index}
          date={item.date}
          title={item.title}
          description={item.description}
          isEven={index % 2 === 1}
          index={index}
          updateVisibility={updateVisibility}
        />
      ))}
      
      <style jsx>{`
        .timeline-container {
          position: relative;
          padding: 30px 0;
          min-height: 800px;
        }
        
        .timeline-line {
          position: absolute;
          left: 50%;
          top: 0;
          bottom: 0;
          width: 4px;
          background-color: #ef4444;
          transform: scaleY(0);
          height: 100%;
          transition: transform 0.3s ease;
        }
        
        .timeline-item {
          display: flex;
          justify-content: flex-start;
          margin-bottom: 50px;
          opacity: 0;
          transform: translateY(30px);
          transition: all 0.5s ease;
          position: relative;
        }
        
        .timeline-item-visible {
          opacity: 1;
          transform: translateY(0);
        }
        
        .timeline-item-even {
          flex-direction: row-reverse;
        }
        
        .timeline-item-odd .timeline-content {
          margin-left: 60px;
          text-align: left;
        }
        
        .timeline-item-even .timeline-content {
          margin-right: 60px;
          text-align: right;
        }
        
        .timeline-icon {
          position: absolute;
          left: 50%;
          transform: translateX(-50%);
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background-color: #ef4444;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10;
          transition: all 0.3s ease;
        }
        
        .timeline-content {
          width: 45%;
          padding: 20px;
          border-radius: 8px;
          box-shadow: 0 4px 16px rgba(0,0,0,0.1);
          transition: all 0.3s ease;
          background-color: white;
        }
        
        .timeline-content:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 25px rgba(0,0,0,0.15);
        }
        
        .timeline-date {
          font-size: 24px;
          font-weight: bold;
          margin-bottom: 10px;
          color: #ef4444;
        }
        
        @media (max-width: 768px) {
          .timeline-line {
            left: 30px;
          }
          
          .timeline-item {
            flex-direction: row !important;
          }
          
          .timeline-icon {
            left: 30px;
          }
          
          .timeline-content {
            width: calc(100% - 80px);
            margin-left: 60px !important;
            text-align: left !important;
          }
        }
      `}</style>
    </div>
  );
};

export default AnimatedTimeline;